import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import calculateFloatingPosition from '../../../utils/floatingPosition';
import './index.scss';

const mergeAriaDescribedBy = (existing, next) => {
  if (!next) {
    return existing;
  }
  if (!existing) {
    return next;
  }
  return `${existing} ${next}`.trim();
};

const DEFAULT_TOOLTIP_Z_INDEX = 7000;

const InfoIcon = ({
  text,
  size = 16,
  className = '',
  children,
  portalTooltip = false,
  tooltipZIndex = DEFAULT_TOOLTIP_Z_INDEX,
}) => {
  const tooltipId = useId();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, left: 0 });
  const [verticalPlacement, setVerticalPlacement] = useState('above');
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const canUseDOM = typeof document !== 'undefined';
  const usePortalTooltip = portalTooltip && canUseDOM;
  const safeTooltipZIndex = Number.isFinite(tooltipZIndex) ? tooltipZIndex : DEFAULT_TOOLTIP_Z_INDEX;

  const showTooltip = useCallback(() => setIsTooltipVisible(true), []);
  const hideTooltip = useCallback(() => setIsTooltipVisible(false), []);
  const handleBlur = useCallback(
    (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        hideTooltip();
      }
    },
    [hideTooltip]
  );

  const describedBy = isTooltipVisible ? tooltipId : undefined;

  const updateTooltipPosition = useCallback(() => {
    if (!usePortalTooltip) return;

    const container = containerRef.current;
    const tooltip = tooltipRef.current;
    if (!container || !tooltip) return;

    const triggerRect = container.getBoundingClientRect();
    const nextCoords = calculateFloatingPosition({
      container: document.documentElement,
      dropdown: tooltip,
      anchor: {
        top: triggerRect.top,
        left: triggerRect.left,
        width: triggerRect.width,
        height: triggerRect.height,
      },
      gap: 8,
      offset: { x: 0, y: 8 },
      align: 'center',
      flip: true,
      strategy: 'fixed',
    });

    const tooltipHeight = tooltip.getBoundingClientRect().height || tooltip.offsetHeight || 0;
    const placedAbove = nextCoords.top + tooltipHeight <= triggerRect.top + 1;

    setTooltipCoords(nextCoords);
    setVerticalPlacement(placedAbove ? 'above' : 'below');
  }, [usePortalTooltip]);

  useLayoutEffect(() => {
    if (!isTooltipVisible || !usePortalTooltip) return undefined;

    updateTooltipPosition();
    const raf = requestAnimationFrame(updateTooltipPosition);

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isTooltipVisible, updateTooltipPosition, usePortalTooltip]);

  useEffect(() => {
    if (!isTooltipVisible || !usePortalTooltip) return undefined;

    const closeTooltip = () => setIsTooltipVisible(false);
    const handleScroll = (event) => {
      const scrollTarget = event?.target;
      if (
        tooltipRef.current
        && scrollTarget
        && typeof Node !== 'undefined'
        && scrollTarget instanceof Node
        && tooltipRef.current.contains(scrollTarget)
      ) {
        return;
      }
      closeTooltip();
    };

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', closeTooltip);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', closeTooltip);
    };
  }, [isTooltipVisible, usePortalTooltip]);

  const containerProps = {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: handleBlur,
    ...(!children ? { tabIndex: 0, 'aria-describedby': describedBy } : {}),
  };

  const renderContent = () => {
    if (children) {
      if (React.isValidElement(children)) {
        const mergedDescribedBy = describedBy
          ? mergeAriaDescribedBy(children.props['aria-describedby'], describedBy)
          : children.props['aria-describedby'];
        return (
          <div className="info-icon">
            {React.cloneElement(children, {
              'aria-describedby': mergedDescribedBy,
            })}
          </div>
        );
      }
      return <div className="info-icon">{children}</div>;
    }

    return (
      <div className="info-icon info-icon--glyph" style={{ width: size, height: size }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width={size}
          height={size}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
      </div>
    );
  };

  const tooltipClassName = [
    'info-icon__tooltip',
    isTooltipVisible ? 'is-visible' : '',
    usePortalTooltip ? 'info-icon__tooltip--floating' : '',
    verticalPlacement === 'below' ? 'info-icon__tooltip--below' : '',
  ].filter(Boolean).join(' ');

  const tooltipStyle = usePortalTooltip
    ? {
      top: tooltipCoords.top,
      left: tooltipCoords.left,
      '--info-icon-tooltip-z-index': `${safeTooltipZIndex}`,
    }
    : undefined;

  const tooltipNode = (
    <div
      id={tooltipId}
      ref={tooltipRef}
      className={tooltipClassName}
      role="tooltip"
      aria-hidden={!isTooltipVisible}
      style={tooltipStyle}
    >
      {text}
    </div>
  );

  return (
    <div ref={containerRef} className={`info-icon-container ${className}`} {...containerProps}>
      {renderContent()}
      {usePortalTooltip ? createPortal(tooltipNode, document.body) : tooltipNode}
    </div>
  );
};

InfoIcon.propTypes = {
  text: PropTypes.string.isRequired,
  size: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
  portalTooltip: PropTypes.bool,
  tooltipZIndex: PropTypes.number,
};

export default InfoIcon;
